import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Paiements.css';
import { FiSearch, FiDownload, FiEye, FiCalendar } from 'react-icons/fi';
import { usePaiements } from '../hooks/usePaiements';
import { useSearch } from '../contexts/SearchContext';
import { useServices } from '../hooks/useServices';

const PaiementsPage = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const { services: servicesList } = useServices();
  const [filters, setFilters] = useState({
    search: '',
    type: 'Tous',
    paymentMethod: 'Tous',
    service: 'Tous',
    date: '',
    month: 'Mois'
  });
  const [activeFilters, setActiveFilters] = useState({
    type: 'Tous',
    paymentMethod: 'Tous',
    service: 'Tous'
  });

  const { 
    paiements, 
    stats, 
    loading, 
    error, 
    refetch,
    downloadCSV 
  } = usePaiements();

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    refetch(newFilters);
  };

  const handleFilterClick = (filterType, value) => {
    setActiveFilters(prev => ({ ...prev, [filterType]: value }));
    handleFilterChange(filterType, value);
  };

  const handleViewDetails = (id) => {
    navigate(`/paiements/${id}`);
  };

  const handleDownloadCSV = async (id) => {
    try {
      await downloadCSV(id);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Réussi': return 'status-green';
      case 'Échoué': return 'status-red';
      case 'En attente': return 'status-orange';
      default: return '';
    }
  };

  if (loading) {
    return <div className="loading">Chargement des paiements...</div>;
  }

  if (error) {
    return <div className="error">Erreur lors du chargement: {error}</div>;
  }

  return (
    <div className="paiements-container">
      <h2>Paiements</h2>

      <div className="paiements-header">
        <div className="month-selector">
          <select 
            value={filters.month}
            onChange={(e) => handleFilterChange('month', e.target.value)}
          >
            <option value="Mois">Mois</option>
            <option value="Janvier">Janvier</option>
            <option value="Février">Février</option>
            <option value="Mars">Mars</option>
            <option value="Avril">Avril</option>
            <option value="Mai">Mai</option>
            <option value="Juin">Juin</option>
            <option value="Juillet">Juillet</option>
            <option value="Août">Août</option>
            <option value="Septembre">Septembre</option>
            <option value="Octobre">Octobre</option>
            <option value="Novembre">Novembre</option>
            <option value="Décembre">Décembre</option>
          </select>
        </div>

        <div className="revenue-summary">
          <div className="revenue-detail">
            <span className="label">Total des revenus générés :</span>
            <div className="revenue-breakdown">
              <div className="revenue-item">
                <span className="type">Transport :</span>
                <span className="amount">{formatAmount(stats.revenue.transport)} CFA</span>
              </div>
              <div className="revenue-item">
                <span className="type">Livraison :</span>
                <span className="amount">{formatAmount(stats.revenue.livraison)} CFA</span>
              </div>
              <div className="revenue-item">
                <span className="type">Location BTP :</span>
                <span className="amount">{formatAmount(stats.revenue.location)} CFA</span>
              </div>
              <div className="revenue-item">
                <span className="type">Conducteur :</span>
                <span className="amount">{formatAmount(stats.revenue.conducteur)} CFA</span>
              </div>
            </div>
          </div>

          <div className="stats-cards">
            <div className="stat-card">
              <h4>Total commission encaissées</h4>
              <div className="stat-value">{formatAmount(stats.totalCommission)} <span>CFA</span></div>
            </div>
            <div className="stat-card">
              <h4>Taux de remboursement</h4>
              <div className="stat-value">{stats.refundRate} <span>%</span></div>
            </div>
            <div className="stat-card">
              <h4>Nbr paiements échoués</h4>
              <div className="stat-value">{stats.failedPayments}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="paiements-filters">
        <div className="search-container">
          <div className="search-box">
            <FiSearch />
            <input 
              type="text" 
              placeholder="Recherche..." 
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="date-selector">
            <span>Date du transaction</span>
            <div className="date-input">
              <FiCalendar />
              <input 
                type="date" 
                className="date-field"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="filter-tabs">
          <div className="filter-group">
            {['Tous', 'Commission', 'Remboursement'].map(type => (
              <button 
                key={type}
                className={`filter-btn ${activeFilters.type === type ? 'active' : ''}`}
                onClick={() => handleFilterClick('type', type)}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="filter-group">
            {['Tous', 'Carte', 'Virement'].map(method => (
              <button 
                key={method}
                className={`filter-btn ${activeFilters.paymentMethod === method ? 'active' : ''}`}
                onClick={() => handleFilterClick('paymentMethod', method)}
              >
                {method}
              </button>
            ))}
          </div>
          <div className="filter-group">
            <button
              className={`filter-btn ${activeFilters.service === 'Tous' ? 'active' : ''}`}
              onClick={() => handleFilterClick('service', 'Tous')}
            >
              Tous
            </button>
            {servicesList && servicesList.length > 0 ? (
              servicesList.slice(0, 4).map(service => (
                <button
                  key={service.id || service.name}
                  className={`filter-btn ${activeFilters.service === (service.name || service) ? 'active' : ''}`}
                  onClick={() => handleFilterClick('service', service.name || service)}
                >
                  {service.name || service}
                </button>
              ))
            ) : (
              <span className="loading-filters">Chargement...</span>
            )}
          </div>
        </div>
      </div>

      <div className="paiements-list">
        {paiements.filter(paiement => {
          // Filtrage par recherche globale (du header)
          if (globalSearch) {
            const searchLower = globalSearch.toLowerCase();
            return (
              paiement.reference?.toLowerCase().includes(searchLower) ||
              paiement.client?.toLowerCase().includes(searchLower) ||
              paiement.prestataire?.toLowerCase().includes(searchLower) ||
              paiement.service?.toLowerCase().includes(searchLower) ||
              paiement.paymentMethod?.toLowerCase().includes(searchLower) ||
              paiement.amount?.toString().includes(searchLower)
            );
          }
          return true;
        }).map((paiement) => (
          <div key={paiement.id} className="paiement-card">
            <div className="card-header">
              <h3># {paiement.reference}</h3>
              <input type="checkbox" className="card-checkbox" />
            </div>

            <div className="card-content">
              <div className="amount-section">
                <div className={`amount ${paiement.status === 'Réussi' ? 'success' : 'fail'}`}>
                  {formatAmount(paiement.amount)} CFA
                </div>
                <div className="payment-type">{paiement.type}</div>
                <div className="commission-info">
                  <span className="label">Commission prélevé</span>
                  <div className="commission-details">
                    <span className="commission-amount">{formatAmount(paiement.commission)} CFA</span>
                    <span className="commission-rate">{paiement.commissionRate}%</span>
                  </div>
                </div>
                <div className="payment-method">
                  <span className="method-badge">{paiement.paymentMethod}</span>
                </div>
              </div>

              <div className="transaction-info">
                <div className="transaction-type">{paiement.service} (ID#{paiement.serviceId})</div>
                <div className="transaction-date">
                  <FiCalendar />
                  {formatDate(paiement.date)}
                </div>
              </div>

              <div className="participants-section">
                <div className="participant">
                  <img src={paiement.client?.avatar || '/default-avatar.png'} alt="Client" className="participant-avatar" />
                  <div className="participant-info">
                    <div className="participant-name">{paiement.client?.name}</div>
                    <div className="participant-type">Client particulier</div>
                  </div>
                </div>
                <a 
                  href="#" 
                  className="participant-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/clients/${paiement.client?.id}`);
                  }}
                >
                  <FiEye /> Fiche détail
                </a>
              </div>

              <div className="participants-section">
                <div className="participant">
                  <img src={paiement.prestataire?.avatar || '/default-avatar.png'} alt="Prestataire" className="participant-avatar" />
                  <div className="participant-info">
                    <div className="participant-name">{paiement.prestataire?.name}</div>
                    <div className="participant-type">Personne morale</div>
                  </div>
                </div>
                <a 
                  href="#" 
                  className="participant-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/prestataires/${paiement.prestataire?.id}`);
                  }}
                >
                  <FiEye /> Fiche détail
                </a>
                <div className="mission-id">ID Mission : {paiement.missionId}</div>
              </div>

              <div className="card-actions">
                <button 
                  className="action-btn csv-btn"
                  onClick={() => handleDownloadCSV(paiement.id)}
                >
                  <FiDownload /> CSV
                </button>
                <button 
                  className="action-btn details-btn"
                  onClick={() => handleViewDetails(paiement.id)}
                >
                  Détails
                </button>
              </div>

              {paiement.dueDate && (
                <div className="due-date-badge">
                  Échéance : {formatDate(paiement.dueDate)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaiementsPage;
